import { z } from 'zod'
import type { Column, Table } from './columns.ts'
import { tablesByName, type TableName } from './tables.ts'

// Postgres renders timestamptz as e.g. 2026-09-27T05:04:47.466123+00:00.
const timestamptz = z.iso.datetime({ offset: true })
// Any 8-4-4-4-12 hex id (not only RFC 4122 variants).
const id = z.guid()

function valueSchema(col: Column): z.ZodType {
  let schema: z.ZodType
  switch (col.kind) {
    case 'uuid':
      schema = id
      break
    case 'text':
      schema = col.values ? z.enum(col.values as [string, ...string[]]) : z.string()
      break
    case 'int':
    case 'bigint':
      schema = z.int()
      break
    case 'bool':
      schema = z.boolean()
      break
    case 'date':
      schema = z.iso.date()
      break
    case 'timestamptz':
      schema = timestamptz
      break
    case 'json':
      schema = z.json()
      break
  }
  return col.nullable ? schema.nullable() : schema
}

function build(table: Table, mode: 'row' | 'writable') {
  const shape: Record<string, z.ZodType> = {}
  for (const [name, col] of Object.entries(table.columns)) {
    if (col.server) {
      if (mode === 'row') shape[name] = valueSchema(col).nullable().optional()
      continue
    }
    shape[name] = valueSchema(col)
  }
  return z.object(shape)
}

const rowCache = new Map<string, z.ZodObject>()
const writableCache = new Map<string, z.ZodObject>()

/** Schema for rows pulled from the server or read locally (unknown keys are stripped). */
export function rowSchema(name: TableName): z.ZodObject {
  let schema = rowCache.get(name)
  if (!schema) rowCache.set(name, (schema = build(tablesByName[name], 'row')))
  return schema
}

/** Schema for rows a client writes (server-set columns are not allowed). */
export function writableSchema(name: TableName): z.ZodObject {
  let schema = writableCache.get(name)
  if (!schema) writableCache.set(name, (schema = build(tablesByName[name], 'writable').strict()))
  return schema
}
